import {
    database,
    listenData,
    readData,
} from '../../../components/shared/database'
import {
    setSuperAdminCompaniesAction,
    setSuperAdminUsersAction,
} from '../../../reducers/actions/super-admin'
import {
    generateUUID,
    ransomString,
} from '../../../components/shared/uuid-generator'
import type {
    User,
    Company,
} from '../../../types/domain'

export type { User, Company }

/**
 * Read users from database and save it into store
 *
 * @return {Promise}
 */
export const readUsers = (): Promise<void> => (
    listenData('user', result => setSuperAdminUsersAction(result as User[]))
)

/**
 * Create user
 *
 * @param {User} user
 * @return {Promise}
 */
export const createUser = (user: User): Promise<unknown> => {
    const userPassword = user.password || ransomString(8)

    if (!user.email || user.email === '') {
        return new Promise(() => {
            throw 'Chybí email!'
        })
    }

    return (
        database('new_user')
            .add({
                displayName: `${user.name} ${user.surname}`,
                email: user.email,
                password: userPassword,
                phoneNumber: !user.phone || user.phone === '' ? null : user.phone,
                approved: true,
                defaultPasswordChanged: false,
                name: user.name,
                surname: user.surname,
                company: user.company,
                isSuperAdmin: user.superAdmin,
                alarms: user.alarms,
                alarmsNotifiable: user.alarmsNotifiable,
                emailSubject: 'Vytvoření nového účtu',
                emailHtml: `
                    Dobrý den,
                    <br/><br/>
                    v aplikaci TopGain Vám byl vytvořen účet
                    <br/><br/>
                    <b>login:</b>&nbsp;${user.email}
                    <br/><br/>
                    <p>Heslo si nastavíte na <a href="https://dohled.top-gain.cz/cs/first-login?email=${user.email}">https://dohled.top-gain.cz/cs/first-login?email=${user.email}</a></p>
                    <p>Po odeslání formuláře pokračujte s instrukcemi na emailu.</p>
                    <br/><br/>
                    Odkaz na aplikaci: <a href="https://dohled.top-gain.cz" target="_blank" rel="noreferrer noopener">https://dohled.top-gain.cz</a>
                `,
            })
    )
}

/**
 * Update user data
 *
 * @param {User} user
 * @param {Partial<User>} data
 * @return {Promise<void>}
 */
export const updateUser = (user: User, data: Partial<User>): Promise<void> => (
    database('user')
        .doc(user.uid)
        .set({
            ...user,
            ...data,
        })
)

/**
 * Delete user
 *
 * @param {User} user
 * @return {Promise<void>}
 */
export const deleteUser = (user: User): Promise<void> => (
    database('user')
        .doc(user.uid)
        .delete()
)

/**
 * Disable or un-disable user
 *
 * @param {User} user
 * @param {boolean} approved
 * @return {Promise<void>}
 */
export const toggleDisableUser = (user: User, approved: boolean): Promise<void> => (
    database('user')
        .doc(user.uid)
        .update({
            approved: approved,
        })
)

/**
 * Read companies from database and save it into store
 *
 * @return {Promise}
 */
export const readCompanies = (): Promise<void> => (
    readData('company')
        .then(data => setSuperAdminCompaniesAction(data as Company[]))
)

/**
 * Save company into database
 *
 * @param {Partial<Company>} company
 * @return {Promise<void>}
 */
export const saveCompany = (company: Partial<Company>): Promise<void> => {
    const companyToSave = {
        ...company,
        uid: company.uid || generateUUID(),
    }

    return database('company')
        .doc(companyToSave.uid)
        .set(companyToSave)
}

/**
 * Delete company from database and remote it from users too
 *
 * @param {Company} company
 * @return {Promise}
 */
export const deleteCompany = (company: Company): Promise<[void, void] | void> => {
    if (!company.uid) {
        return new Promise(resolve => resolve())
    }

    const usersPromise = database('user')
        .where('company', '==', company.uid)
        .get()
        .then(querySnapshot => {
            querySnapshot.forEach((doc) => {
                database('user')
                    .doc(doc.id)
                    .update({
                        company: '',
                    })
            })
        })

    const companyPromise = database('company')
        .doc(company.uid)
        .delete()

    return Promise.all([
        usersPromise,
        companyPromise,
    ])
}
